/**
 * EventTimeline (DESIGN.md T9): the vertical "what happened to this item" list shared by the message trace, rule
 * history and change history. One step per event: a status dot on a connecting line, the step name, its time, an
 * optional tag (event id) and meta line, and optional content below (details, raw fields). i18n-free: every text
 * comes from the caller.
 */
import type { ReactNode } from 'react';
import { cn } from '../lib/utils';
import type { Status } from './states';

export type TimelineTone = Status;

export interface EventTimelineItem {
  id: string;
  /** Step name in the shared vocabulary (received, scanned, quarantined, released, delivered, deferred, …). */
  title: ReactNode;
  /** Already formatted time (callers use formatDate). */
  time: string;
  /** Machine-readable time for <time dateTime>. */
  dateTime?: string;
  tone: TimelineTone;
  /** Small tag after the title, e.g. the event id (MAIL-1010). */
  tag?: ReactNode;
  /** One line under the title: source, actor, recipient, reason. */
  meta?: ReactNode;
  /** Content below the step (details, links, raw fields toggle). */
  children?: ReactNode;
}

export interface EventTimelineProps {
  items: readonly EventTimelineItem[];
  /** Accessible name of the list. */
  label: string;
  className?: string;
}

const dot: Readonly<Record<TimelineTone, string>> = {
  ok: 'bg-success',
  warning: 'bg-warning',
  error: 'bg-destructive',
  unknown: 'bg-muted-foreground',
  inactive: 'bg-border',
};

export function EventTimeline({ items, label, className }: EventTimelineProps) {
  return (
    <ol aria-label={label} className={cn('flex flex-col', className)}>
      {items.map((item, index) => (
        <li key={item.id} data-tone={item.tone} className="relative flex gap-3 pb-4 last:pb-0">
          {index < items.length - 1 && <span aria-hidden className="absolute top-4 bottom-0 left-[5px] w-px bg-border" />}
          <span aria-hidden className={cn('relative mt-1.5 size-[11px] shrink-0 rounded-full ring-2 ring-background', dot[item.tone])} />
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
              <span className="font-medium">{item.title}</span>
              {item.tag && <span className="font-mono text-xs text-muted-foreground">{item.tag}</span>}
              <time dateTime={item.dateTime} className="ml-auto text-xs whitespace-nowrap text-muted-foreground tabular-nums">
                {item.time}
              </time>
            </div>
            {item.meta && <div className="text-sm break-words text-muted-foreground">{item.meta}</div>}
            {item.children}
          </div>
        </li>
      ))}
    </ol>
  );
}
